import { useState } from 'react';
import { Send, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { logError } from '@/utils/logger';
import { submitErrorMessage } from '@/utils/submitError';
import { INPUT_CLS, LABEL_CLS, ERROR_CLS } from '@/components/brand/BrandKit';
import { LEAD_FORM_T } from '@/components/forms/leadFormI18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', company: '', phone: '', email: '', message: '' };

function Field({ label, children }) {
  return <div><span className={LABEL_CLS}>{label}</span>{children}</div>;
}

function LeadSuccess({ testid, t, isAr }) {
  return (
    <div data-testid={`${testid}-success`} dir={isAr ? 'rtl' : 'ltr'} className="rounded-2xl border border-[#EE5A01]/30 bg-white p-10 text-center shadow-sm">
      <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#EE5A01] text-white"><Check className="w-8 h-8" /></span>
      <h3 className="mt-5 font-heading font-black text-2xl text-[#121212]">{t.successTitle}</h3>
      <p className="mt-2 font-body text-sm text-[#666666]">{t.successBody}</p>
    </div>
  );
}

function LeadFields({ testid, t, form, set }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t.name}><Input data-testid={`${testid}-name`} value={form.name} onChange={set('name')} placeholder={t.namePh} className={INPUT_CLS} /></Field>
        <Field label={t.company}><Input data-testid={`${testid}-company`} value={form.company} onChange={set('company')} placeholder={t.companyPh} className={INPUT_CLS} /></Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t.phone}><Input data-testid={`${testid}-phone`} value={form.phone} onChange={set('phone')} placeholder="+971 XX XXX XXXX" className={INPUT_CLS} dir="ltr" /></Field>
        <Field label={t.email}><Input data-testid={`${testid}-email`} type="email" value={form.email} onChange={set('email')} placeholder={t.emailPh} className={INPUT_CLS} dir="ltr" /></Field>
      </div>
      <Field label={t.message}><Textarea data-testid={`${testid}-message`} value={form.message} onChange={set('message')} placeholder={t.messagePh} className={`${INPUT_CLS} h-auto min-h-[110px]`} /></Field>
    </>
  );
}

function useLeadSubmit({ enquiryType, source, form, t, isAr }) {
  const [status, setStatus] = useState({ submitted: false, submitting: false, error: '' });
  const submit = async () => {
    setStatus({ submitted: false, submitting: true, error: '' });
    try {
      await axios.post(`${API}/contact`, {
        full_name: form.name, phone: form.phone, email: form.email, company: form.company || '',
        enquiry_type: enquiryType,
        message: `[${source || enquiryType}] ${form.message || t.noDetails}`,
      });
      setStatus({ submitted: true, submitting: false, error: '' });
    } catch (err) {
      logError('SimpleLeadForm', err);
      setStatus({ submitted: false, submitting: false, error: submitErrorMessage(err, isAr) });
    }
  };
  return { ...status, submit };
}

export function SimpleLeadForm({ enquiryType, source = '', title, intro, testid = 'lead-form', lang = 'en' }) {
  const t = LEAD_FORM_T[lang] || LEAD_FORM_T.en;
  const isAr = lang === 'ar';
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const ready = Boolean(form.name && form.phone && form.email);
  const { submitted, submitting, error, submit } = useLeadSubmit({ enquiryType, source, form, t, isAr });

  if (submitted) return <LeadSuccess testid={testid} t={t} isAr={isAr} />;

  return (
    <div data-testid={testid} dir={isAr ? 'rtl' : 'ltr'} className="rounded-2xl border border-black/5 bg-white p-6 sm:p-8 shadow-[0_16px_40px_rgba(0,0,0,0.06)]">
      {title && <h3 className="font-heading font-bold text-lg text-[#121212]">{title}</h3>}
      {intro && <p className="mt-1 font-body text-sm text-[#666666]">{intro}</p>}
      <div className="mt-5 space-y-4">
        <LeadFields testid={testid} t={t} form={form} set={set} />
        {error && <p data-testid={`${testid}-error`} role="alert" className={ERROR_CLS}>{error}</p>}
        <button data-testid={`${testid}-submit`} onClick={() => ready && submit()} disabled={submitting || !ready} className="btn-primary w-full disabled:opacity-50">
          <Send className="w-4 h-4" /> {submitting ? t.submitting : t.submit}
        </button>
      </div>
    </div>
  );
}
